import React, { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, TrendingUp } from "lucide-react";
import { track } from "../../lib/analytics";

// Curated, plausible social-proof entries (first name only, no PII leaked).
// Mix of roles, goals, and Indian + global B2B geos to match dotConverse's footprint.
const ENTRIES = [
  { name: "Priya", role: "Head of Growth", co: "SaaS", city: "Bangalore", goal: "requested a pipeline plan", mins: 3 },
  { name: "Rahul", role: "VP Marketing", co: "Fintech", city: "Mumbai", goal: "booked a strategy call", mins: 7 },
  { name: "Ananya", role: "Demand Gen Lead", co: "B2B SaaS", city: "Gurgaon", goal: "downloaded the ABM playbook", mins: 12 },
  { name: "Michael", role: "CMO", co: "Manufacturing", city: "Singapore", goal: "requested a pipeline plan", mins: 18 },
  { name: "Karan", role: "Marketing Director", co: "Cloud", city: "Pune", goal: "ran the pipeline calculator", mins: 24 },
  { name: "Sarah", role: "Head of Demand Gen", co: "MarTech", city: "London", goal: "booked a strategy call", mins: 31 },
  { name: "Vikram", role: "Founder", co: "B2B SaaS", city: "Hyderabad", goal: "requested a pipeline plan", mins: 42 },
  { name: "Neha", role: "VP Growth", co: "Enterprise SaaS", city: "Delhi", goal: "ran the pipeline calculator", mins: 55 },
  { name: "James", role: "Head of ABM", co: "Industrial", city: "New York", goal: "requested a pipeline plan", mins: 68 },
  { name: "Divya", role: "Growth Lead", co: "Healthtech", city: "Chennai", goal: "booked a strategy call", mins: 82 },
];

const FIRST_DELAY = 22000; // 22s initial dwell before first toast
const INTERVAL = 32000; // ~32s between toasts
const VISIBLE_FOR = 6500; // each toast visible 6.5s
const MAX_SHOW = 5; // cap per session
const STORAGE_KEY = "dc_social_proof_done_v1";

export default function SocialProofToast() {
  const [current, setCurrent] = useState(null);
  const indexRef = useRef(0);
  const shownRef = useRef(0);
  const hideTimerRef = useRef(null);
  const cycleTimerRef = useRef(null);

  useEffect(() => {
    // Don't re-run in the same session if we already completed the cap
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "done") return;
    } catch {
      /* no-op */
    }

    // Randomize starting entry so visitors see variety
    indexRef.current = Math.floor(Math.random() * ENTRIES.length);

    const showNext = () => {
      if (shownRef.current >= MAX_SHOW) {
        try {
          sessionStorage.setItem(STORAGE_KEY, "done");
        } catch {
          /* no-op */
        }
        return;
      }
      const entry = ENTRIES[indexRef.current % ENTRIES.length];
      indexRef.current += 1;
      shownRef.current += 1;
      setCurrent(entry);
      track("social_proof_toast_shown", { name: entry.name, goal: entry.goal });

      hideTimerRef.current = window.setTimeout(() => {
        setCurrent(null);
      }, VISIBLE_FOR);

      cycleTimerRef.current = window.setTimeout(showNext, INTERVAL);
    };

    const firstTimer = window.setTimeout(showNext, FIRST_DELAY);

    return () => {
      window.clearTimeout(firstTimer);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      if (cycleTimerRef.current) window.clearTimeout(cycleTimerRef.current);
    };
  }, []);

  const dismiss = () => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    setCurrent(null);
    track("social_proof_toast_dismissed");
  };

  return (
    <div
      className="fixed bottom-5 left-5 z-[90] pointer-events-none"
      aria-live="polite"
    >
      <AnimatePresence mode="wait">
        {current && (
          <motion.div
            key={`${current.name}-${shownRef.current}`}
            data-testid="social-proof-toast"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto max-w-[340px] bg-white/95 backdrop-blur-xl border border-black/5 rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)] px-4 py-3.5 flex items-start gap-3"
          >
            <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#FFB000] to-[#E96A7F] flex items-center justify-center text-white text-sm font-semibold">
              {current.name[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] leading-snug text-[#0A0A0A]">
                <span className="font-semibold">{current.name}</span>
                <span className="text-black/55">
                  {" "}
                  from a {current.co} in {current.city}
                </span>
              </div>
              <div className="text-[12px] text-black/55 mt-0.5 truncate">
                just {current.goal}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#0FB5A6] font-medium">
                <TrendingUp size={10} strokeWidth={2.4} />
                <span>{current.mins}m ago</span>
              </div>
            </div>
            <button
              onClick={dismiss}
              data-testid="social-proof-close"
              aria-label="Dismiss"
              className="shrink-0 w-6 h-6 -mt-1 -mr-1 rounded-full hover:bg-black/5 flex items-center justify-center text-black/40 hover:text-black/70 transition-colors"
            >
              <X size={13} strokeWidth={2} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
